import { Badge, Box, Button, HStack, Stack, Text } from "@chakra-ui/react";
import { useMemo, type ReactNode } from "react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { FiArrowRight, FiXCircle } from "react-icons/fi";
import type { BuyerPublicBidDetailDto } from "../../../../autogen/bff-api";
import { DownloadDocumentIconButton } from "../../../../common/documents/DownloadDocumentIconButton/DownloadDocumentIconButton";
import { useDisplayDateWithTime } from "../../../../common/formatting/useDisplayDateWithTime";
import { TipTap } from "../../../../common/input/TipTap/TipTap";
import { getTiptapJsonFromMarkdown } from "../../../../common/input/TipTap/markdownUtils";
import { SectionCard } from "../../../sourcing-events/portal/view-single/SectionCard";

export const RejectedSummary = ({
  detail,
  onViewDetails,
}: {
  detail: BuyerPublicBidDetailDto;
  onViewDetails?: () => void;
}) => {
  const { t } = useTranslation();
  const displayDateTime = useDisplayDateWithTime();
  const rejection = detail.bid.rejection;
  const rejectedMeta = formatRejectedMeta({
    rejectedAt: rejection?.rejectedAt,
    rejectedBy: rejection?.rejectedBy,
    displayDateTime,
    t,
  });

  return (
    <SectionCard
      icon={FiXCircle}
      title={t("Avvist leverandør")}
      borderColor="red.100"
      headerBg="red.50"
      headerBorderColor="red.100"
      iconColor="red.600"
    >
      <Stack spacing="3">
        {rejectedMeta ? (
          <Text fontSize="sm" color="smMuted">
            {rejectedMeta}
          </Text>
        ) : null}

        {rejection?.grounds?.length ? (
          <Text fontSize="sm" color="smText">
            {t("{{count}} lovhjemmel valgt", {
              count: rejection.grounds.length,
            })}
          </Text>
        ) : (
          <Text fontSize="sm" color="smMuted">
            {t("Det er ikke lagret detaljer for denne avvisningen.")}
          </Text>
        )}

        {onViewDetails ? (
          <Button
            alignSelf="flex-start"
            size="sm"
            variant="link"
            colorScheme="teal"
            rightIcon={<FiArrowRight />}
            onClick={onViewDetails}
          >
            {t("Se avvisningsdetaljer")}
          </Button>
        ) : null}
      </Stack>
    </SectionCard>
  );
};

export const RejectionDetailsSection = ({
  detail,
}: {
  detail: BuyerPublicBidDetailDto;
}) => {
  const { t } = useTranslation();
  const displayDateTime = useDisplayDateWithTime();
  const rejection = detail.bid.rejection;
  const messageContent = useMemo(
    () =>
      rejection?.message ? getTiptapJsonFromMarkdown(rejection.message) : "",
    [rejection?.message],
  );
  const rejectedMeta = formatRejectedMeta({
    rejectedAt: rejection?.rejectedAt,
    rejectedBy: rejection?.rejectedBy,
    displayDateTime,
    t,
  });

  if (!rejection) return null;

  return (
    <Box id="bid-rejection-details" scrollMarginTop="6">
      <SectionCard icon={FiXCircle} title={t("Avvisningsdetaljer")}>
        <Stack spacing="3">
          {rejectedMeta ? (
            <Text fontSize="sm" color="smMuted">
              {rejectedMeta}
            </Text>
          ) : null}

          {rejection.grounds?.length ? (
            <Stack spacing="2">
              {rejection.grounds.map((ground) => (
                <Box
                  key={ground.key}
                  border="1px solid"
                  borderColor="red.100"
                  rounded="md"
                  px="3"
                  py="2"
                  bg="red.50"
                >
                  <HStack spacing="2" align="center" flexWrap="wrap">
                    <Text fontSize="sm" fontWeight="semibold" color="smText">
                      {ground.foaReference}
                    </Text>
                    <Badge colorScheme={ground.mandatory ? "red" : "orange"}>
                      {ground.mandatory ? t("SKAL") : t("KAN")}
                    </Badge>
                  </HStack>
                  <Text fontSize="sm" color="smMuted">
                    {ground.label}
                  </Text>
                </Box>
              ))}
            </Stack>
          ) : (
            <Text fontSize="sm" color="smMuted">
              {t("Det er ikke lagret detaljer for denne avvisningen.")}
            </Text>
          )}

          {rejection.reason && (
            <StoredTextBlock title={t("Begrunnelse")}>
              <Text fontSize="sm" color="smText" whiteSpace="pre-wrap">
                {rejection.reason}
              </Text>
            </StoredTextBlock>
          )}

          {rejection.message && (
            <StoredTextBlock title={t("Melding til leverandør")}>
              <Box
                sx={{
                  ".tiptap p": {
                    marginBottom: "0.75rem",
                  },
                  ".tiptap p:last-child": {
                    marginBottom: 0,
                  },
                  ".tiptap strong": {
                    fontWeight: "semibold",
                  },
                }}
              >
                <TipTap
                  content={messageContent}
                  editable={false}
                  hasBorder={false}
                  maxHeight="96"
                  padding="0"
                />
              </Box>
            </StoredTextBlock>
          )}

          {rejection.attachments?.length ? (
            <Box>
              <Text
                fontSize="xs"
                color="smMuted"
                fontWeight="semibold"
                textTransform="uppercase"
                letterSpacing="wide"
              >
                {t("Vedlegg")}
              </Text>
              <Stack spacing="2" mt="2">
                {rejection.attachments.map((document) => (
                  <HStack
                    key={document.id}
                    spacing="2"
                    justify="space-between"
                    border="1px solid"
                    borderColor="smBorder"
                    rounded="md"
                    px="3"
                    py="2"
                  >
                    <Text fontSize="sm" color="smText" isTruncated>
                      {document.name}
                    </Text>
                    <DownloadDocumentIconButton
                      documentId={document.id}
                      size="sm"
                    />
                  </HStack>
                ))}
              </Stack>
            </Box>
          ) : rejection.attachmentIds?.length ? (
            <Text fontSize="sm" color="smMuted">
              {t("Vedlegg kunne ikke lastes.")}
            </Text>
          ) : null}
        </Stack>
      </SectionCard>
    </Box>
  );
};

const StoredTextBlock = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => (
  <Box>
    <Text
      fontSize="xs"
      color="smMuted"
      fontWeight="semibold"
      textTransform="uppercase"
      letterSpacing="wide"
      mb="2"
    >
      {title}
    </Text>
    <Box
      border="1px solid"
      borderColor="smBorder"
      rounded="md"
      bg="smBackground"
      px="4"
      py="3"
    >
      {children}
    </Box>
  </Box>
);

const formatRejectedMeta = ({
  rejectedAt,
  rejectedBy,
  displayDateTime,
  t,
}: {
  rejectedAt?: string | null;
  rejectedBy?: string | null;
  displayDateTime: (value: string) => string;
  t: TFunction;
}) => {
  if (rejectedAt && rejectedBy) {
    return t("Avvist {{date}} av {{person}}", {
      date: displayDateTime(rejectedAt),
      person: rejectedBy,
    });
  }
  if (rejectedAt) {
    return t("Avvist {{date}}", { date: displayDateTime(rejectedAt) });
  }
  if (rejectedBy) {
    return t("Avvist av {{person}}", { person: rejectedBy });
  }
  return null;
};
